import { useEffect, useMemo, useState } from "react";
import { History, Filter, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const ACTIONS = [
  { value: "all", label: "Todas ações" },
  { value: "create", label: "Criação" },
  { value: "update", label: "Atualização" },
  { value: "delete", label: "Exclusão (lixeira)" },
  { value: "restore", label: "Restauração" },
  { value: "hard_delete", label: "Exclusão permanente" },
];

const ACTION_TONE = {
  create: "bg-emerald-100 text-emerald-700",
  update: "bg-blue-100 text-blue-700",
  delete: "bg-amber-100 text-amber-700",
  restore: "bg-[#EDE1F3] text-[#7B569A]",
  hard_delete: "bg-rose-100 text-rose-700",
};

const COLLECTIONS = [
  ["all", "Todas coleções"],
  ["users", "Usuários"],
  ["praticantes", "Praticantes"],
  ["responsaveis", "Responsáveis"],
  ["terapeutas", "Terapeutas"],
  ["cavalos", "Cavalos"],
  ["cavalo_registros", "Registros de cavalo"],
  ["estoque_itens", "Itens de estoque"],
  ["lista_espera", "Lista de espera"],
  ["sessoes", "Sessões"],
  ["mensalidades", "Mensalidades"],
  ["salarios", "Salários"],
  ["evolucoes", "Evoluções"],
  ["fichas_avaliacao", "Fichas de avaliação"],
];

function fmtDateTime(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

function fmtValue(v) {
  if (v === null || v === undefined) return <span className="text-slate-400 italic">(vazio)</span>;
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (Array.isArray(v)) return v.length ? JSON.stringify(v) : <span className="text-slate-400 italic">(vazio)</span>;
  if (typeof v === "object") return <pre className="text-[10px] whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>;
  return String(v);
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const changes = log.changes || {};
  const changeCount = Object.keys(changes).length;
  return (
    <div className="border-b border-border last:border-0" data-testid={`audit-row-${log.id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-4 py-3 hover:bg-slate-50 grid grid-cols-12 gap-3 items-center"
      >
        <div className="col-span-3 text-xs text-slate-500">{fmtDateTime(log.timestamp)}</div>
        <div className="col-span-2">
          <Badge className={`${ACTION_TONE[log.action] || "bg-slate-100 text-slate-700"} border-0 text-[10px] uppercase tracking-wider`}>
            {log.action}
          </Badge>
        </div>
        <div className="col-span-2 text-sm font-medium text-slate-800">{log.collection_label || log.collection}</div>
        <div className="col-span-3 text-sm text-slate-600 truncate">
          <span className="font-medium text-slate-700">{log.user_name || "—"}</span>
          <span className="text-slate-400 ml-1 text-xs">({log.user_role})</span>
        </div>
        <div className="col-span-2 flex items-center justify-end gap-1 text-xs text-slate-500">
          {changeCount > 0 && <span>{changeCount} campo{changeCount > 1 ? "s" : ""}</span>}
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 bg-slate-50/60">
          <div className="text-[11px] text-slate-500 mb-2">
            ID do registro: <code className="bg-white px-1.5 py-0.5 rounded">{log.document_id || "-"}</code>
            {log.user_email && <> · Por: <code className="bg-white px-1.5 py-0.5 rounded">{log.user_email}</code></>}
          </div>
          {changeCount === 0 ? (
            <p className="text-xs text-slate-500 italic">Sem mudanças em campos rastreáveis.</p>
          ) : (
            <div className="rounded-lg bg-white border border-border overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider">
                  <tr>
                    <th className="text-left px-3 py-2 w-1/4">Campo</th>
                    <th className="text-left px-3 py-2">Antes</th>
                    <th className="text-left px-3 py-2">Depois</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(changes).map(([field, val]) => (
                    <tr key={field} className="border-t border-border align-top">
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{field}</td>
                      <td className="px-3 py-2 text-rose-700">{fmtValue(val.before)}</td>
                      <td className="px-3 py-2 text-emerald-700">{fmtValue(val.after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Auditoria() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action: "all",
    collection: "all",
    q: "",
    date_from: "",
    date_to: "",
  });
  const [summary, setSummary] = useState({ total: 0, by_action: {} });

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.action !== "all") params.action = filters.action;
      if (filters.collection !== "all") params.collection = filters.collection;
      if (filters.q) params.q = filters.q;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [{ data: rows }, { data: sum }] = await Promise.all([
        api.get("/audit-logs", { params }),
        api.get("/audit-logs/summary"),
      ]);
      setLogs(rows);
      setSummary(sum);
    } catch (e) {
      toast.error("Erro ao carregar auditoria");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="auditoria-page">
      <PageHeader
        icon={History}
        title="Auditoria"
        description="Registro completo de criações, edições, exclusões e restaurações no sistema."
        actions={
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="auditoria-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
          </Button>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <StatBox label="Total de eventos" value={summary.total} tone="turquoise" />
        <StatBox label="Criações" value={summary.by_action.create || 0} tone="emerald" />
        <StatBox label="Atualizações" value={summary.by_action.update || 0} tone="blue" />
        <StatBox label="Exclusões" value={summary.by_action.delete || 0} tone="amber" />
        <StatBox label="Restaurações" value={summary.by_action.restore || 0} tone="lilac" />
      </div>

      <Card className="mb-4 border-border shadow-none">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
            <Filter className="h-3.5 w-3.5" /> Filtros
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Ação</Label>
              <Select value={filters.action} onValueChange={(v) => setF("action", v)}>
                <SelectTrigger data-testid="auditoria-filter-action"><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] uppercase text-muted-foreground">Coleção</Label>
              <Select value={filters.collection} onValueChange={(v) => setF("collection", v)}>
                <SelectTrigger data-testid="auditoria-filter-collection"><SelectValue /></SelectTrigger>
                <SelectContent>{COLLECTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">De</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="auditoria-filter-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Até</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="auditoria-filter-to" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Usuário (email/nome)</Label>
              <Input value={filters.q} onChange={(e) => setF("q", e.target.value)} placeholder="Buscar…" data-testid="auditoria-filter-q" />
            </div>
            <div className="md:col-span-6 flex justify-end">
              <Button onClick={fetchData} className="bg-brand-turquoise hover:bg-[#3A9CA2] text-white" data-testid="auditoria-apply">Aplicar filtros</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-16 text-center text-muted-foreground">Carregando…</div>
          ) : logs.length === 0 ? (
            <div className="p-16 text-center text-muted-foreground">Nenhum registro encontrado.</div>
          ) : (
            <div>
              <div className="px-4 py-2 bg-slate-100 text-[10px] uppercase tracking-wider text-slate-500 grid grid-cols-12 gap-3 font-semibold">
                <div className="col-span-3">Data / Hora</div>
                <div className="col-span-2">Ação</div>
                <div className="col-span-2">Módulo</div>
                <div className="col-span-3">Usuário</div>
                <div className="col-span-2 text-right">Mudanças</div>
              </div>
              {logs.map((l) => <LogRow key={l.id} log={l} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const TONE_MAP = {
  turquoise: "bg-[#DFF4F5] text-[#2A7A80]",
  emerald: "bg-emerald-50 text-emerald-700",
  blue: "bg-blue-50 text-blue-700",
  amber: "bg-amber-50 text-amber-700",
  lilac: "bg-[#EDE1F3] text-[#7B569A]",
};

function StatBox({ label, value, tone }) {
  return (
    <div className={`rounded-xl p-4 ${TONE_MAP[tone]}`} data-testid={`auditoria-stat-${label}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="font-heading text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
